import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import { api, type PlanKind, type PlanStatus } from '../lib/api';
import { Card, CardHeader, Table, Th, Tr, Td, Badge, Select, ActionButton, Spinner, EmptyState, Modal } from '../components/ui';

const STATUS_VARIANT: Record<PlanStatus, 'gray' | 'amber' | 'blue' | 'green'> = {
  draft: 'gray', submitted: 'amber', approved: 'blue', locked: 'green', superseded: 'gray', restated: 'gray',
};

const inputStyle: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};

export default function AdminFpaPlansPage() {
  const { user } = useAuth();
  const canWrite = user?.fpa_role === 'analyst' || user?.fpa_role === 'approver';
  const isApprover = user?.fpa_role === 'approver';
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery(() => api.fpa.plans.list());
  const [rowErr, setRowErr] = useState<string | null>(null);

  async function clone(p: { id: string; name: string; kind: PlanKind }) {
    const name = window.prompt('Name for the cloned plan:', `${p.name} (copy)`);
    if (!name) return;
    setRowErr(null);
    try { const { plan } = await api.fpa.plans.clone(p.id, { name, kind: p.kind }); navigate(`/admin/fpa/plans/${plan.id}`); }
    catch (e) { setRowErr((e as Error).message); }
  }
  async function remove(p: { id: string; name: string }) {
    if (!window.confirm(`Delete plan "${p.name}"? This removes all its inputs and outputs and cannot be undone.`)) return;
    setRowErr(null);
    try { await api.fpa.plans.remove(p.id); refetch(); }
    catch (e) { setRowErr((e as Error).message); }
  }

  const [name, setName] = useState('');
  const [kind, setKind] = useState<PlanKind>('budget');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [wizard, setWizard] = useState(false);
  const [showBackfill, setShowBackfill] = useState(false);

  async function create() {
    setFormErr(null);
    setBusy(true);
    try {
      const { plan } = await api.fpa.plans.create({ name, kind, horizon_start: from, horizon_end: to });
      setName(''); setFrom(''); setTo('');
      refetch();
      navigate(`/admin/fpa/plans/${plan.id}`);
    } catch (err) { setFormErr((err as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <Card>
      <CardHeader>
        <div style={{ fontSize: 13, fontWeight: 500 }}>FP&amp;A plans</div>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
          {isApprover && <ActionButton onClick={() => setShowBackfill(true)}>⚙ Migrate fixed costs → items</ActionButton>}
          {canWrite && <ActionButton variant="primary" onClick={() => setWizard(true)}>✨ New budget (guided)</ActionButton>}
        </span>
      </CardHeader>
      {wizard && <NewBudgetWizard onClose={() => setWizard(false)} onCreated={(id) => navigate(`/admin/fpa/plans/${id}`)} />}
      {showBackfill && <BackfillModal onClose={() => { setShowBackfill(false); refetch(); }} />}

      {canWrite && (
        <div style={{ padding: '10px 16px', borderBottom: '0.5px solid var(--color-border)', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>or build manually:</span>
          <input placeholder="Plan name" value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, width: 200 }} />
          <Select value={kind} onChange={(v) => setKind(v as PlanKind)}>
            <option value="budget">Budget</option>
            <option value="forecast">Forecast</option>
            <option value="goal">Goal</option>
          </Select>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Horizon</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle} />
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>→</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle} />
          <ActionButton variant="primary" disabled={busy || !name || !from || !to} onClick={create}>New plan</ActionButton>
          {formErr && <span style={{ fontSize: 11, color: 'var(--color-red-text)' }}>{formErr}</span>}
        </div>
      )}

      {rowErr && <div style={{ padding: '6px 16px', fontSize: 11, color: 'var(--color-red-text)' }}>{rowErr}</div>}
      {isLoading ? <Spinner />
        : error ? <EmptyState message={error} />
        : !data || data.plans.length === 0 ? <EmptyState message="No plans yet." />
        : (
          <Table>
            <thead><tr><Th>Name</Th><Th>Kind</Th><Th>Status</Th><Th>Horizon</Th>{canWrite && <Th> </Th>}</tr></thead>
            <tbody>
              {data.plans.map((p) => (
                <Tr key={p.id} onClick={() => navigate(`/admin/fpa/plans/${p.id}`)}>
                  <Td bold>{p.name}</Td>
                  <Td><Badge variant="gray">{p.kind}</Badge></Td>
                  <Td><Badge variant={STATUS_VARIANT[p.status]}>{p.status}</Badge></Td>
                  <Td muted>{p.horizon_start} → {p.horizon_end}</Td>
                  {canWrite && (
                    <Td>
                      <span style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                        <ActionButton onClick={() => clone(p)}>Clone</ActionButton>
                        {isApprover && ['draft', 'superseded', 'restated'].includes(p.status) && <ActionButton variant="danger" onClick={() => remove(p)}>Delete</ActionButton>}
                      </span>
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
    </Card>
  );
}

// ─── Guided new-budget wizard ─────────────────────────────────────────────────

type StepState = { label: string; status: 'pending' | 'running' | 'done' | 'skip'; note?: string };

function NewBudgetWizard({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const year = new Date().getFullYear();
  const [name, setName] = useState(`FY${year} budget`);
  const [from, setFrom] = useState(`${year}-01-01`);
  const [to, setTo] = useState(`${year}-12-31`);
  const [importStaff, setImportStaff] = useState(true);
  const [importFixed, setImportFixed] = useState(true);
  const [util, setUtil] = useState('0.75');
  const [rate, setRate] = useState('150');
  const [hours, setHours] = useState('160');
  const depts = useQuery(() => api.fpa.departments());
  const [running, setRunning] = useState(false);
  const [steps, setSteps] = useState<StepState[]>([]);
  const [err, setErr] = useState<string | null>(null);

  function patch(i: number, s: Partial<StepState>) { setSteps((prev) => prev.map((x, j) => j === i ? { ...x, ...s } : x)); }

  async function run() {
    setErr(null); setRunning(true);
    const plan: StepState[] = [
      { label: 'Create budget', status: 'pending' },
      { label: importStaff ? 'Import staff from users' : 'Import staff (skipped)', status: importStaff ? 'pending' : 'skip' },
      { label: importFixed ? 'Import fixed costs from QBO' : 'Import fixed costs (skipped)', status: importFixed ? 'pending' : 'skip' },
      { label: 'Seed capacity drivers', status: 'pending' },
      { label: 'Recompute', status: 'pending' },
    ];
    setSteps(plan);
    try {
      patch(0, { status: 'running' });
      const { plan: created } = await api.fpa.plans.create({ name, kind: 'budget', horizon_start: from, horizon_end: to });
      patch(0, { status: 'done' });

      if (importStaff) {
        patch(1, { status: 'running' });
        const r = await api.fpa.plans.importHeadcountFromUsers(created.id);
        patch(1, { status: 'done', note: `${r.created} added` });
      }
      if (importFixed) {
        patch(2, { status: 'running' });
        const r = await api.fpa.plans.importFixedCostsFromQbo(created.id);
        patch(2, { status: 'done', note: `${r.written} values · ${r.months} months` });
      }

      patch(3, { status: 'running' });
      const entries: Array<{ assumption_key: string; department_id?: string | null; value_numeric: number }> = [
        { assumption_key: 'capacity.hours_per_head', value_numeric: Number(hours) || 160 },
      ];
      for (const d of depts.data?.departments ?? []) {
        entries.push({ assumption_key: 'capacity.utilisation', department_id: d.id, value_numeric: Number(util) || 0.75 });
        entries.push({ assumption_key: 'capacity.bill_rate', department_id: d.id, value_numeric: Number(rate) || 150 });
      }
      await api.fpa.plans.setAssumptions(created.id, entries);
      patch(3, { status: 'done', note: `${(depts.data?.departments ?? []).length} departments` });

      patch(4, { status: 'running' });
      await api.fpa.plans.recompute(created.id);
      patch(4, { status: 'done' });

      onCreated(created.id);
    } catch (e) {
      setErr((e as Error).message);
      setSteps((prev) => prev.map((x) => x.status === 'running' ? { ...x, status: 'pending' } : x));
    } finally { setRunning(false); }
  }

  const mark = (s: StepState['status']) => s === 'done' ? '✓' : s === 'running' ? '…' : s === 'skip' ? '–' : '○';
  return (
    <Modal title="New budget" subtitle="Start from your real staff and actuals, then tune" onClose={onClose} width={560}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label style={{ fontSize: 12 }}>Name<input value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputStyle, width: '100%', marginTop: 4 }} /></label>
        <div style={{ display: 'flex', gap: 10 }}>
          <label style={{ flex: 1, fontSize: 12 }}>Horizon start<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...inputStyle, width: '100%', marginTop: 4 }} /></label>
          <label style={{ flex: 1, fontSize: 12 }}>Horizon end<input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...inputStyle, width: '100%', marginTop: 4 }} /></label>
        </div>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}><input type="checkbox" checked={importStaff} onChange={(e) => setImportStaff(e.target.checked)} /> Import current staff from users (salaries left at 0 to fill in)</label>
        <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}><input type="checkbox" checked={importFixed} onChange={(e) => setImportFixed(e.target.checked)} /> Import fixed costs from QBO actuals</label>
        <div style={{ display: 'flex', gap: 10, fontSize: 12 }}>
          <label style={{ flex: 1 }}>Utilisation<input value={util} onChange={(e) => setUtil(e.target.value)} style={{ ...inputStyle, width: '100%', marginTop: 4 }} /></label>
          <label style={{ flex: 1 }}>Bill rate<input value={rate} onChange={(e) => setRate(e.target.value)} style={{ ...inputStyle, width: '100%', marginTop: 4 }} /></label>
          <label style={{ flex: 1 }}>Hours/mo<input value={hours} onChange={(e) => setHours(e.target.value)} style={{ ...inputStyle, width: '100%', marginTop: 4 }} /></label>
        </div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Seeds these capacity drivers for every department as a starting point — tune them per-department in Build afterwards.</div>

        {steps.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 12px', background: 'var(--color-bg)', borderRadius: 'var(--radius-sm)', border: '0.5px solid var(--color-border)' }}>
            {steps.map((s, i) => (
              <div key={i} style={{ fontSize: 12, display: 'flex', gap: 8, color: s.status === 'skip' ? 'var(--color-text-hint)' : 'var(--color-text)' }}>
                <span style={{ width: 14 }}>{mark(s.status)}</span><span>{s.label}</span>
                {s.note && <span style={{ color: 'var(--color-text-muted)' }}>· {s.note}</span>}
              </div>
            ))}
          </div>
        )}
        {err && <div style={{ fontSize: 12, color: 'var(--color-red-text)' }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <ActionButton onClick={onClose} disabled={running}>Cancel</ActionButton>
          <ActionButton variant="primary" onClick={run} disabled={running || !name || !from || !to}>{running ? 'Creating…' : 'Create budget'}</ActionButton>
        </div>
      </div>
    </Modal>
  );
}

// ─── One-shot Planning-v2 cutover: fixed-cost assumptions → grid items ────────
// Approver-gated migration tool. Dry-run on open, then Apply. Safe to remove
// (this component + the header button + the /fpa/backfill/fixed-costs route)
// once run against production.
type BackfillRow = { plan_id: string; name: string; status: string; items_created: number; assumptions_removed: number; totals_match: boolean | null };

function BackfillModal({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<BackfillRow[] | null>(null);
  const [applied, setApplied] = useState(false);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  async function run(apply: boolean) {
    setBusy(true); setErr(null);
    try { const r = await api.fpa.backfillFixedCosts({ apply }); setRows(r.plans); setApplied(apply); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }
  // Dry-run on mount.
  useEffect(() => { void run(false); }, []);

  const totalItems = (rows ?? []).reduce((s, r) => s + r.items_created, 0);
  const anyMismatch = (rows ?? []).some((r) => r.totals_match === false);

  return (
    <Modal title="Migrate fixed costs → grid items" subtitle="Converts each cost.fixed.* assumption into a manual Budget Grid item (draft/submitted plans only). Recompute output is unchanged." onClose={onClose} width={620}>
      <div style={{ padding: 20, fontSize: 12 }}>
        {err && <p style={{ color: 'var(--color-red-text)' }}>{err}</p>}
        {busy && !rows && <Spinner />}
        {rows && (
          <>
            <div style={{ marginBottom: 8, color: 'var(--color-text-muted)' }}>
              {applied ? 'Applied.' : 'Preview'} — {rows.length} plan(s), {totalItems} item(s) {applied ? 'created' : 'to create'}.
              {anyMismatch && <span style={{ color: 'var(--color-amber)' }}> ⚠ some totals differ — investigate before relying on the grid.</span>}
            </div>
            <Table>
              <thead><Tr><Th>Plan</Th><Th>Status</Th><Th right>Items</Th><Th right>Assumptions removed</Th><Th>Totals</Th></Tr></thead>
              <tbody>
                {rows.map((r) => (
                  <Tr key={r.plan_id}>
                    <Td>{r.name}</Td>
                    <Td>{r.status}</Td>
                    <Td right>{r.items_created}</Td>
                    <Td right>{applied ? r.assumptions_removed : '—'}</Td>
                    <Td>{r.totals_match === null ? '—' : r.totals_match ? '✓ match' : '✗ differ'}</Td>
                  </Tr>
                ))}
                {rows.length === 0 && <Tr><Td>Nothing to migrate.</Td><Td> </Td><Td> </Td><Td> </Td><Td> </Td></Tr>}
              </tbody>
            </Table>
          </>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <ActionButton onClick={onClose} disabled={busy}>{applied ? 'Done' : 'Cancel'}</ActionButton>
          {!applied && <ActionButton variant="primary" onClick={() => run(true)} disabled={busy || !rows || totalItems === 0}>{busy ? 'Working…' : `Apply (${totalItems} item${totalItems === 1 ? '' : 's'})`}</ActionButton>}
        </div>
      </div>
    </Modal>
  );
}
